import {format, addDays, startOfWeek, parseISO, isValid, getHours, getMinutes} from 'date-fns';

/**
 * Utilitaires de gestion des dates et heures pour le calendrier wellness
 *
 * Centralise toutes les fonctions de manipulation temporelle :
 * - Conversion et validation des dates
 * - Formatage des heures et durées
 * - Calcul des jours fermés et heures d'ouverture
 */

/**
 * Convertit une valeur en objet Date valide
 * @param {Date|string|number} dateValue - Valeur à convertir (Date, string ISO ou timestamp)
 * @return {Date|null} - Date valide ou null
 */
export function ensureDate(dateValue) {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : null;
  }

  // Timestamp numérique (ex: {$date: 1751808000000} des exports MongoDB) :
  // parseISO n'accepte que du texte
  if (typeof dateValue === 'number') {
    const date = new Date(dateValue);
    return isValid(date) ? date : null;
  }

  try {
    const date = parseISO(dateValue);
    return isValid(date) ? date : null;
  } catch (err) {
    return null;
  }
}

/**
 * Formate une date/heure en chaîne hh:mm
 * Supporte les objets Date et les heures décimales (ex: 14.5 = 14h30)
 * @param {Date|string|number} dateValue - Date à formater ou heure décimale
 * @param {string} fallback - Valeur par défaut si la date est invalide
 * @return {string} - Heure formatée (HH:mm)
 */
export function formatTime(dateValue, fallback = '--:--') {
  // Gestion des heures décimales (ex: 14.5 = 14h30)
  if (typeof dateValue === 'number') {
    const hours = Math.floor(dateValue);
    const minutes = Math.round((dateValue % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Gestion des objets Date
  const validDate = ensureDate(dateValue);
  if (!validDate) return fallback;

  try {
    return format(validDate, 'HH:mm');
  } catch (err) {
    return fallback;
  }
}

/**
 * Formate une durée en minutes vers un format lisible
 * Supporte les durées simples et les plages de durées
 * @param {number} minutes - Durée en minutes
 * @param {number} maxMinutes - Durée maximale pour créer une plage (optionnel)
 * @return {string} - Durée formatée (ex: "1h30", "45min", "30-60min")
 */
export function formatDuration(minutes, maxMinutes = null) {
  // Gestion des plages de durées
  if (maxMinutes && maxMinutes !== minutes) {
    const minFormatted = formatDuration(minutes);
    const maxFormatted = formatDuration(maxMinutes);
    return `${minFormatted}-${maxFormatted}`;
  }

  // Gestion des durées simples
  if (!minutes || minutes <= 0) return '0min';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h${mins}`;
}

/**
 * Génère une liste de créneaux horaires pour une journée
 * @param {number} stepMinutes - Intervalle en minutes entre chaque créneau (défaut: 15)
 * @param {number} startHour - Heure de début (défaut: 0)
 * @param {number} endHour - Heure de fin (défaut: 24)
 * @return {string[]} - Tableau de créneaux au format HH:mm
 */
export function generateTimeSlots(stepMinutes = 15, startHour = 0, endHour = 24) {
  const slots = [];

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      const decimalHour = hour + minute / 60;
      slots.push(formatTime(decimalHour));
    }
  }

  return slots;
}

/**
 * Obtenir la plage de jours pour une semaine (lundi-dimanche)
 * @param {Date|string} date - Date dans la semaine
 * @return {Date[]} - Tableau de 7 jours
 */
export function getWeekDays(date) {
  const validDate = ensureDate(date);
  if (!validDate) return [];

  const start = startOfWeek(validDate, {weekStartsOn: 1});
  return Array.from({length: 7}, (_, i) => addDays(start, i));
}

/**
 * Calcule l'heure décimale à partir d'une Date
 * @param {Date} date - Objet Date
 * @return {number} - Heure au format décimal (ex: 14.5 pour 14h30)
 */
export function getDecimalHour(date) {
  const validDate = ensureDate(date);
  if (!validDate) return 0;

  return getHours(validDate) + (getMinutes(validDate) / 60);
}

/**
 * Formate une date en string YYYY-MM-DD sans conversion timezone
 * Utilise les valeurs locales de la date pour éviter les décalages UTC
 * @param {Date|string} date - Date à formater
 * @return {string|null} - Date formatée ou null si invalide
 */
export function formatDateWithoutTimezone(date) {
  if (!date) return null;

  const validDate = ensureDate(date);
  if (!validDate) return null;

  const year = validDate.getFullYear();
  const month = String(validDate.getMonth() + 1).padStart(2, '0');
  const day = String(validDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}
